/**
 * Per-feature user-facing copy.
 * @package @app/billing-config
 *
 * Single source of truth for the labels and upgrade messaging shown when a
 * user hits a plan gate. Used by:
 *   - the /pricing modal that explains *which* limit was hit
 *   - any future in-app feature gate UI
 *   - server-side ORPCError messages
 *
 * Keep these strings short — they render inside a modal alongside a CTA.
 */

import type { PlanFeatures } from './types';

export interface FeatureDescription {
  /** Short label used in headings and toasts ("AI extractions", "Brand matching"). */
  label: string;
  /** Sentence describing what the limit is on the free plan. */
  freeLimitCopy: string;
  /** Sentence describing what Pro unlocks. */
  proBenefitCopy: string;
  /** Plan we should send the user to as the upsell. */
  suggestedPlan: 'pro' | 'agent-solo' | 'agent-team';
}

export const FEATURE_DESCRIPTIONS: Record<keyof PlanFeatures, FeatureDescription> = {
  deals: {
    label: 'Active deals',
    freeLimitCopy: 'The Free plan caps you at 5 active deals.',
    proBenefitCopy: 'Pro unlocks unlimited deals.',
    suggestedPlan: 'pro',
  },
  aiExtractions: {
    label: 'AI extractions',
    freeLimitCopy: 'The Free plan includes 3 AI extractions per month.',
    proBenefitCopy: 'Pro unlocks unlimited AI extractions and contract summaries.',
    suggestedPlan: 'pro',
  },
  brandMatching: {
    label: 'Brand matching',
    freeLimitCopy: 'Brand matching is not available on the Free plan.',
    proBenefitCopy: 'Pro unlocks brand match scores and discovery.',
    suggestedPlan: 'pro',
  },
  analytics: {
    label: 'Advanced analytics',
    freeLimitCopy: 'Advanced analytics is not available on the Free plan.',
    proBenefitCopy: 'Pro unlocks the analytics dashboard.',
    suggestedPlan: 'pro',
  },
  export: {
    label: 'Data export',
    freeLimitCopy: 'Data export is not available on the Free plan.',
    proBenefitCopy: 'Pro unlocks CSV export.',
    suggestedPlan: 'pro',
  },
  sms: {
    label: 'SMS notifications',
    freeLimitCopy: 'SMS notifications are not available on the Free plan.',
    proBenefitCopy: 'Pro unlocks SMS deal alerts.',
    suggestedPlan: 'pro',
  },
  // Agent / university features — Pro doesn't grant these; the suggested
  // upsell is Solo (smallest tier that does).
  athletes: {
    label: 'Managed athletes',
    freeLimitCopy: 'Managing athletes requires an Agent plan.',
    proBenefitCopy: 'Agent Solo includes up to 10 athletes.',
    suggestedPlan: 'agent-solo',
  },
  seats: {
    label: 'Team seats',
    freeLimitCopy: 'Team seats require an Agent plan.',
    proBenefitCopy: 'Agent Team includes 5 seats.',
    suggestedPlan: 'agent-team',
  },
  commissionTracking: {
    label: 'Commission tracking',
    freeLimitCopy: 'Commission tracking requires an Agent plan.',
    proBenefitCopy: 'Agent Solo unlocks commission tracking and reconciliation.',
    suggestedPlan: 'agent-solo',
  },
  recruitingDataset: {
    label: 'Recruiting dataset',
    freeLimitCopy: 'The enriched athlete recruiting dataset requires an Agent plan.',
    proBenefitCopy: 'Agent Solo unlocks dataset browse, filters, and one-click prospect import.',
    suggestedPlan: 'agent-solo',
  },
  whiteLabel: {
    label: 'White-label reports',
    freeLimitCopy: 'White-label reports require an Agent Team plan.',
    proBenefitCopy: 'Agent Team unlocks white-label reports.',
    suggestedPlan: 'agent-team',
  },
  api: {
    label: 'API access',
    freeLimitCopy: 'API access requires an Agent Team plan.',
    proBenefitCopy: 'Agent Team unlocks API access.',
    suggestedPlan: 'agent-team',
  },
  ferpa: {
    label: 'FERPA dashboard',
    freeLimitCopy: 'FERPA features require a University plan.',
    proBenefitCopy: 'University plans unlock FERPA-safe compliance dashboards.',
    // No athlete-tier upsell makes sense for org-only features; the UI
    // should special-case these. The type still requires a value here.
    suggestedPlan: 'pro',
  },
  sso: {
    label: 'SSO',
    freeLimitCopy: 'SSO requires a University Standard or higher plan.',
    proBenefitCopy: 'University Standard unlocks SSO.',
    suggestedPlan: 'pro',
  },
};

/**
 * Type guard: is the given string a valid PlanFeatures key?
 * Use this to validate untrusted query params before indexing FEATURE_DESCRIPTIONS.
 */
export function isFeatureName(name: string): name is keyof PlanFeatures {
  return name in FEATURE_DESCRIPTIONS;
}
